import { useEffect, useState } from "react";
import {
  Menu, X, MapPin, Star, CheckCircle, ChevronDown,
  MessageSquare, Heart, Award, Users, Globe, Brain,
  Mic, Baby, BookOpen, Waves, Phone,
} from "lucide-react";
import { Reveal } from "./components/site/Reveal";
import { NavWaveform } from "./components/site/NavWaveform";
import { ConsentBanner } from "./components/site/ConsentBanner";
import { ContactForm } from "./components/site/ContactForm";
import { useParallax } from "./hooks/useParallax";
import { openConsentPreferences } from "./lib/consent";

/**
 * Alt text describes only what is visible. These are stock photographs of real
 * children — nothing here asserts a diagnosis or a therapy relationship.
 */
const childMoments = [
  {
    src: "/child1.jpeg",
    width: 1080,
    height: 1620,
    alt: "A toddler in patterned pyjamas standing at home, carefully opening a small illustrated box",
    caption: "Birth to age 3",
    body: "Early intervention woven into the routines your family already has — play, meals, books, bath time.",
  },
  {
    src: "/child3.jpeg",
    width: 1080,
    height: 1620,
    alt: "A young toddler in a high chair reaching for the coloured beads of a wooden abacus",
    caption: "Play-based sessions",
    body: "Therapy built around what already holds your child’s attention, so practice never feels like work.",
  },
  {
    src: "/child2.jpeg",
    width: 1080,
    height: 1438,
    alt: "A smiling young boy in a grey long-sleeved shirt, seated and looking towards the camera",
    caption: "Every child, every ability",
    body: "From first words to AAC and social language, we meet your child exactly where they are.",
  },
];

const navLinks = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#faq", label: "FAQ" },
  { href: "#contact", label: "Contact" },
];

const services = [
  {
    icon: Baby,
    title: "Early Intervention",
    subtitle: "Birth to Age 3",
    description:
      "Early identification and treatment of speech, language, and feeding delays in infants and toddlers. The first three years of life represent the brain's most critical development window, and intervention during this period produces the best long-term outcomes. We deliver therapy within your child's natural daily routines.",
    conditions: ["Late talkers", "Developmental delays", "Parent coaching & training", "Birth–3 home-based services"],
  },
  {
    icon: MessageSquare,
    title: "Language Therapy",
    subtitle: "Expressive & Receptive",
    description:
      "Comprehensive assessment and treatment for children who struggle to understand language (receptive) or to express their thoughts, needs, and ideas (expressive). We target vocabulary, sentence structure, grammar, following directions, and narrative skills across home, school, and community settings.",
    conditions: ["Delayed language milestones", "Following multi-step directions", "Vocabulary gaps", "Sentence formulation"],
  },
  {
    icon: Mic,
    title: "Articulation & Phonology",
    subtitle: "Speech Sound Disorders",
    description:
      "Evidence-based treatment for children who produce speech sounds incorrectly or who have patterns of sound errors that reduce their intelligibility. Whether your child has a single sound error like a lisp or widespread errors affecting multiple sounds, we create individualized plans to build clarity and confidence.",
    conditions: ["Lisps", "R sound errors", "Multiple sound substitutions", "Phonological patterns"],
  },
  {
    icon: Brain,
    title: "Autism & Social Language",
    subtitle: "ASD & Pragmatic Skills",
    description:
      "Specialized therapy for children on the autism spectrum and those with pragmatic language challenges. We target social communication, perspective-taking, joint attention, conversational turn-taking, and understanding nonverbal cues in ways that are meaningful and functional for your child.",
    conditions: ["Autism spectrum disorder", "Social communication differences", "Pragmatic language", "Peer interaction skills"],
  },
  {
    icon: Globe,
    title: "AAC Services",
    subtitle: "Augmentative & Alternative Communication",
    description:
      "Assessment, recommendation, and hands-on training for children who need support beyond speech alone. We work with picture exchange systems (PECS), speech-generating devices, and robust high-tech AAC tools to maximize your child's participation and independence as a communicator.",
    conditions: ["Minimally verbal children", "AAC device training", "PECS", "Speech-generating devices"],
  },
  {
    icon: Waves,
    title: "Fluency / Stuttering",
    subtitle: "Fluency Disorders",
    description:
      "Effective, compassionate treatment for children who stutter or experience other fluency disorders. Our approach combines fluency-shaping techniques, stuttering modification strategies, and counseling to build confidence and positive attitudes toward communication, because how your child feels about talking matters.",
    conditions: ["Childhood stuttering", "Cluttering", "School-age & teen support", "Family education"],
  }
];

const whyChooseUs = [
  {
    icon: Heart,
    title: "Family-Centered Care",
    desc: "We actively involve parents and caregivers in every session, teaching practical strategies that carry over into daily life and accelerate your child's progress.",
  },
  {
    icon: Award,
    title: "18+ Years of Expertise",
    desc: "Melissa Kelly has been serving children and families since 2006, with deep experience spanning a wide range of diagnoses, age groups, and practice settings.",
  },
  {
    icon: MapPin,
    title: "Natural Environment Therapy",
    desc: "We deliver therapy in homes, daycares, and schools, the real environments where children live and learn, so skills generalize faster and last longer.",
  },
  {
    icon: Globe,
    title: "Telehealth Available",
    desc: "Convenient, high-quality virtual sessions bring expert speech therapy to your family wherever you are, with no commute required.",
  },
];

const therapySettings = [
  {
    icon: Heart,
    label: "In Your Home",
    desc: "The most natural setting for infants and toddlers. We weave therapy into daily routines like meals, bath time, and play.",
  },
  {
    icon: BookOpen,
    label: "Daycare & Preschool",
    desc: "On-site sessions at your child's program allow collaboration with teachers and integration with the classroom routine.",
  },
  {
    icon: Users,
    label: "School Settings",
    desc: "Collaboration with educators to support your child's communication goals within the school environment.",
  },
  {
    icon: Globe,
    label: "Telehealth / Virtual",
    desc: "Flexible, effective virtual sessions available to families throughout Florida, with no travel required.",
  },
];

const serviceAreas = [
  "Stuart", "Jensen Beach", "Palm City", "Hobe Sound",
  "Port St. Lucie", "Jupiter", "Indiantown", "Rio",
  "North River Shores", "Martin County", "Treasure Coast",
];

const faqs = [
  {
    q: "How do I know if my child needs speech therapy?",
    a: "Common signs include: not meeting age-appropriate speech and language milestones, being difficult for unfamiliar people to understand, struggling to follow multi-step directions, limited vocabulary compared to peers, stuttering, or difficulties at mealtimes. If you have any concerns about your child's communication development, a free consultation is the ideal first step, with no obligation and no referral required.",
  },
  {
    q: "What ages does Pediatric Speech Solutions serve?",
    a: "We serve children from birth through school age. Early intervention services are available for children birth to 3 years, and therapy is provided for preschool and school-age children as well. Research is clear: earlier identification and intervention leads to significantly better long-term outcomes.",
  },
  {
    q: "Where do therapy sessions take place?",
    a: "We provide therapy in a variety of natural environments, including your home, your child's daycare or preschool, school settings, or via telehealth. Research supports delivering therapy where children actually live and learn, which promotes faster generalization of new skills into everyday life.",
  },
  {
    q: "Do you accept insurance?",
    a: "A superbill can be provided for potential insurance reimbursement. Pediatric Speech Solutions is an approved Step Up provider. Family Empowerment Scholarship for Student with Unique Abilities (FES-UA) ",
  },
  {
    q: "What areas do you serve?",
    a: "We serve families throughout Martin County and the Treasure Coast, including Stuart, Jensen Beach, Palm City, Hobe Sound, Port St. Lucie, Jupiter, and surrounding communities. Telehealth services are available to families statewide in Florida.",
  },
  {
    q: "What is Melissa Kelly's background and training?",
    a: "Melissa Kelly, MA, CCC-SLP holds a Bachelor of Science and a Master of Arts in Communicative Sciences and Disorders from the University of Central Florida. She has been practicing since 2006 with experience in private practice, public schools, telehealth, subacute care, and early intervention services for children birth to age 3.",
  },
  {
    q: "What is early intervention and why does it matter?",
    a: "Early intervention refers to speech, language, and feeding therapy services for children from birth to age 3. The early years are a critical window of brain development, and intervening during this period yields significantly better outcomes than waiting. Services are typically delivered in the home and center on empowering parents as the primary agents of their child's growth.",
  },
  {
    q: "What should I expect at an initial evaluation?",
    a: "An evaluation typically includes a thorough case history review, parent interview, structured and play-based observation of your child, and standardized assessments appropriate for your child's age and concerns. At the end, we discuss findings and recommendations together so you fully understand the next steps.",
  },
  {
    q: "How do I get started?",
    a: "Reach out via our contact form, phone, or social media to schedule a free initial consultation. During that conversation, we will discuss your concerns, your child's history, and whether an evaluation is the right next step. We look forward to connecting with your family.",
  },
];

const SHOW_TESTIMONIALS = false;

const testimonials = [
  {
    quote:
      "Melissa is absolutely wonderful with our son. He was barely speaking at age 2 and after several months working with her, he is talking in full sentences. She is patient, creative, and truly cares about each child she works with.",
    author: "Sarah M.",
    location: "Stuart, FL",
    stars: 5,
  },
  {
    quote:
      "We were so stressed when our daughter was diagnosed with a phonological disorder. Melissa made the whole process feel manageable. She explains everything clearly, involves us in every session, and our daughter actually looks forward to her appointments.",
    author: "James & Tara R.",
    location: "Palm City, FL",
    stars: 5,
  },
  {
    quote:
      "The in-home therapy was a game-changer for our family. Our son was much more comfortable in his own environment, and the progress was faster than we expected. Melissa is a true professional who genuinely loves what she does.",
    author: "Michelle K.",
    location: "Jensen Beach, FL",
    stars: 5,
  },
];

export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const heroParallax = useParallax<HTMLDivElement>(0.12);
  const aboutParallax = useParallax<HTMLDivElement>(0.1);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) {
      // WCAG 2.3.3 — smooth scrolling is motion the user may not want.
      const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    }
    setMenuOpen(false);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <div className="bg-background text-foreground min-h-screen font-body">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2.5 focus:rounded-full focus:text-sm focus:font-semibold"
      >
        Skip to main content
      </a>

      {/* ── NAV ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => scrollTo("#home")} className="flex items-center gap-3 text-left">
            <img src="/logo.png" alt="" className="w-[52px] h-[52px] shrink-0" />
            <span>
              <span className="font-display text-lg font-semibold text-primary leading-tight block">
                Pediatric Speech Solutions
              </span>
              <span className="text-xs text-muted-foreground">Stuart &amp; Treasure Coast, FL</span>
            </span>
          </button>

          <nav aria-label="Primary" className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="group relative text-sm font-medium text-foreground/65 hover:text-primary transition-colors"
              >
                {l.label}
                <NavWaveform />
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contact")}
              className="bg-primary text-primary-foreground text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-primary/85 transition-colors"
            >
              Consultation
            </button>
          </nav>

          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            {menuOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
        </div>

        {menuOpen && (
          <nav
            id="mobile-menu"
            aria-label="Mobile"
            className="md:hidden bg-background border-t border-border px-6 py-5 flex flex-col gap-4"
          >
            {navLinks.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className="text-base font-medium text-foreground/80 hover:text-primary text-left transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contact")}
              className="bg-primary text-primary-foreground text-sm font-semibold px-5 py-3 rounded-full hover:bg-primary/85 transition-colors text-center mt-1"
            >
              Consultation
            </button>
          </nav>
        )}
      </header>

      <main id="main-content">

      {/* ── HERO ── */}
      <section id="home" className="pt-[72px] min-h-screen grid md:grid-cols-2">
        <div className="flex flex-col justify-center px-6 md:px-12 lg:px-20 py-20 max-w-2xl">
          <Reveal>
            <span className="inline-block text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-5">
              Stuart &amp; Treasure Coast, FL
            </span>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-4xl md:text-5xl lg:text-[3.25rem] font-semibold text-foreground leading-[1.15] mb-6">
              Helping Every Child<br />
              <em className="not-italic text-primary">Find Their Voice</em>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-[1.05rem] text-muted-foreground leading-relaxed mb-8 max-w-lg">
              Expert pediatric speech therapy for children from birth through school age, delivered at home, in daycare, at school, and via telehealth throughout the Treasure Coast.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="flex flex-wrap gap-3 mb-12">
              <button
                onClick={() => scrollTo("#contact")}
                className="bg-primary text-primary-foreground font-semibold px-8 py-3.5 rounded-full hover:bg-primary/85 transition-colors text-sm"
              >
                Schedule a Free Consultation
              </button>
              <button
                onClick={() => scrollTo("#services")}
                className="border border-primary/50 text-primary font-semibold px-8 py-3.5 rounded-full hover:bg-secondary transition-colors text-sm"
              >
                View Services
              </button>
            </div>
          </Reveal>
          <Reveal delay={400}>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-6">
              {[
                "Practicing since 2006",
                "In-home & telehealth",
                "Birth through school age",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle size={16} aria-hidden="true" className="text-primary flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <div ref={heroParallax.ref} className="relative bg-secondary overflow-hidden min-h-80 md:min-h-0">
          <img
            src="/heroimage2.jpeg"
            alt="A young girl leaning in to whisper into a smiling boy's ear, both children grinning"
            width={1080}
            height={864}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-[130%] -top-[15%] object-cover"
            style={{ transform: `translateY(${heroParallax.offset}px)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/30 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 bg-background/95 backdrop-blur rounded-xl p-4 shadow-xl">
            <p className="text-sm font-semibold text-primary">Melissa Kelly, MA,CCC-SLP</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Licensed Speech-Language Pathologist · UCF Communicative Sciences &amp; Disorders · Since 2006
            </p>
          </div>
        </div>
      </section>

      {/* ── SERVICE TICKER ── */}
      {/* Scrolls sideways on narrow screens, so it needs its own tab stop
          (WCAG 2.1.1) — a keyboard-only user has no other way to pan it. */}
      <div
        className="bg-primary py-4 overflow-x-auto"
        tabIndex={0}
        role="group"
        aria-label="Services we provide"
      >
        <ul className="max-w-6xl mx-auto px-6 flex flex-nowrap md:flex-wrap gap-x-8 gap-y-2 md:justify-center items-center">
          {services.map((s) => (
            <li
              key={s.title}
              className="text-primary-foreground/80 text-xs font-semibold whitespace-nowrap flex items-center gap-2 tracking-wide uppercase"
            >
              <s.icon size={13} aria-hidden="true" className="text-primary-foreground/50" />
              {s.title}
            </li>
          ))}
        </ul>
      </div>

      {/* ── ABOUT ── */}
      <section id="about" className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal y={40} className="relative">
            <div
              ref={aboutParallax.ref}
              className="relative overflow-hidden rounded-2xl bg-secondary aspect-[4/5] max-w-sm mx-auto md:max-w-none"
            >
              <img
                src="/letters.jpeg"
                alt="Colorful foam alphabet letters set into a wooden puzzle board, a tactile tool used in speech and language therapy"
                width={1080}
                height={720}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-[130%] -top-[15%] object-cover"
                style={{ transform: `translateY(${aboutParallax.offset}px)` }}
              />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-accent text-accent-foreground rounded-2xl p-5 shadow-2xl hidden md:block">
              <p className="text-3xl font-display font-bold leading-none">18+</p>
              <p className="text-sm mt-1.5 font-medium">Years of Experience</p>
            </div>
          </Reveal>

          <Reveal y={40} delay={120}>
            <span className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              Meet Your Therapist
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6 leading-tight">
              Dedicated, Experienced, and Passionate About Children's Communication
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Welcome to Pediatric Speech Solutions! Our practice was founded by{" "}
              <strong className="text-foreground font-semibold">Melissa Kelly, MA,CCC-SLP</strong>, a
              licensed and certified Speech-Language Pathologist who has been helping children
              communicate more effectively since 2006.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Melissa holds both a Bachelor of Science and a Master of Arts in Communicative
              Sciences and Disorders from the University of Central Florida. Her career has spanned
              private practice, public schools, subacute care, telehealth, and early intervention
              with children from birth to age 3.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              At Pediatric Speech Solutions, we believe every child deserves to be heard,
              understood, and equipped with the tools to communicate confidently. We partner
              closely with parents and caregivers every step of the way, because meaningful
              progress happens between sessions, not just during them.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "MA,CCC-SLP", desc: "Master's-level clinician" },
                { label: "UCF Trained", desc: "Communicative Sciences &amp; Disorders" },
                { label: "Birth–School Age", desc: "Full continuum of care" },
                { label: "Telehealth Ready", desc: "Serving all of Florida" },
              ].map((item) => (
                <div key={item.label} className="bg-secondary rounded-xl p-4">
                  <p
                    className="font-semibold text-primary text-sm"
                    dangerouslySetInnerHTML={{ __html: item.label }}
                  />
                  <p
                    className="text-xs text-muted-foreground mt-0.5"
                    dangerouslySetInnerHTML={{ __html: item.desc }}
                  />
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="py-20 md:py-28 bg-secondary px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              What We Treat
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Comprehensive Pediatric Speech Therapy Services
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              From early intervention with infants and toddlers to school-age articulation,
              language, and fluency, we offer a full spectrum of evidence-based services
              tailored to your child's unique needs and goals.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <Reveal
                key={s.title}
                delay={(i % 3) * 90}
                className="bg-card rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300 group border border-border/40"
              >
                <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                  <s.icon size={20} aria-hidden="true" className="text-primary" />
                </div>
                <p className="text-[10px] font-bold text-accent uppercase tracking-[0.18em] mb-1.5">
                  {s.subtitle}
                </p>
                <h3 className="font-display text-lg font-semibold text-foreground mb-3">
                  {s.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {s.description}
                </p>
                <ul className="space-y-1.5">
                  {s.conditions.map((c) => (
                    <li key={c} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-10">
            Not sure which service fits your child? We will help you figure it out during a consultation.{" "}
            <button
              onClick={() => scrollTo("#contact")}
              className="text-primary font-semibold hover:underline"
            >
              Reach out today <span aria-hidden="true">→</span>
            </button>
          </p>
        </div>
      </section>

      {/* ── PHOTO BAND ── */}
      <section aria-labelledby="bubbles-heading" className="relative overflow-hidden">
        <img
          src="/photokids1.jpeg"
          alt="Four children of different ages playing together outdoors among floating soap bubbles"
          width={1080}
          height={699}
          loading="lazy"
          decoding="async"
          className="w-full h-[420px] md:h-[460px] object-cover object-center"
        />
        {/* Solid-enough scrim that the white text clears 4.5:1 regardless of
            what part of the photograph sits behind it. */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/45" />
        <div className="absolute inset-0 flex items-center px-6">
          <Reveal className="max-w-6xl mx-auto w-full">
            <div className="max-w-lg">
              <span className="text-accent-light text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
                Why It Works
              </span>
              <h2
                id="bubbles-heading"
                className="font-display text-3xl md:text-4xl font-semibold text-primary-foreground mb-4 leading-tight"
              >
                Progress Happens in Play
              </h2>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-20 md:py-24 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              Why Choose Us
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              A Different Kind of Practice
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              We go beyond the clinic to meet your child and family where you are.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, i) => (
              <Reveal key={item.title} delay={i * 90} className="text-center px-2">
                <div className="w-14 h-14 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <item.icon size={24} aria-hidden="true" className="text-accent" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── MOMENTS GALLERY ── */}
      <section aria-labelledby="moments-heading" className="pb-20 md:pb-24 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-12">
            <h2
              id="moments-heading"
              className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3"
            >
              Every Child, Every Stage
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
              From a toddler’s first words to a school-age child finding confidence
              in conversation.
            </p>
          </Reveal>

          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {childMoments.map((moment, i) => (
              <li key={moment.src}>
                <Reveal delay={i * 90} y={20}>
                  <figure>
                    <div className="overflow-hidden rounded-2xl bg-secondary aspect-[3/4]">
                      <img
                        src={moment.src}
                        alt={moment.alt}
                        width={moment.width}
                        height={moment.height}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <figcaption className="mt-4">
                      <span className="block font-display text-lg font-semibold text-foreground mb-1.5">
                        {moment.caption}
                      </span>
                      <span className="block text-sm text-muted-foreground leading-relaxed">
                        {moment.body}
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── SETTINGS & AREAS (dark section) ── */}
      <section className="py-20 md:py-24 bg-foreground text-background px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20">
          {/* Settings */}
          <Reveal>
            <span className="text-accent-light text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              Therapy Settings
            </span>
            <h2 className="font-display text-3xl font-semibold mb-5 leading-tight">
              We Come to You
            </h2>
            <p className="text-background/65 leading-relaxed mb-8 text-[0.95rem]">
              Children learn best in the environments where they live, play, and learn. We offer
              flexible session settings so therapy fits your family's life instead of the other way around.
            </p>
            <div className="space-y-5">
              {therapySettings.map((s, i) => (
                <Reveal key={s.label} delay={i * 80} className="flex gap-4">
                  <div className="w-10 h-10 bg-background/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <s.icon size={17} aria-hidden="true" className="text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-background text-sm">{s.label}</p>
                    <p className="text-background/55 text-sm mt-0.5 leading-snug">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>

          {/* Areas */}
          <Reveal delay={150}>
            <span className="text-accent-light text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              Service Area
            </span>
            <h2 className="font-display text-3xl font-semibold mb-5 leading-tight">
              Proudly Serving the Treasure Coast
            </h2>
            <p className="text-background/65 leading-relaxed mb-8 text-[0.95rem]">
              We serve children and families throughout Martin County and the broader Treasure Coast
              region. Telehealth services are available to families anywhere in Florida.
            </p>
            <div className="flex flex-wrap gap-2.5 mb-6">
              {serviceAreas.map((area) => (
                <span
                  key={area}
                  className="bg-background/10 text-background/85 text-sm px-4 py-2 rounded-full border border-background/15 hover:bg-background/15 transition-colors"
                >
                  {area}
                </span>
              ))}
            </div>
            <p className="text-background/55 text-sm">
              Don't see your city listed? Contact us. We may still be able to serve your area or
              connect you via telehealth.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      {SHOW_TESTIMONIALS && (
        <section className="py-20 md:py-28 px-6 bg-secondary">
          <div className="max-w-6xl mx-auto">
            <Reveal className="text-center mb-14">
              <span className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
                Testimonials
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
                What Families Are Saying
              </h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <Reveal key={i} delay={i * 110} className="bg-card rounded-2xl p-7 flex flex-col border border-border/40">
                  <div className="flex gap-1 mb-5" role="img" aria-label={`${t.stars} out of 5 stars`}>
                    {[...Array(t.stars)].map((_, j) => (
                      <Star key={j} size={15} aria-hidden="true" className="text-accent fill-accent" />
                    ))}
                  </div>
                  <p className="text-muted-foreground leading-relaxed italic text-[0.9rem] flex-1 mb-6">
                    "{t.quote}"
                  </p>
                  <div>
                    <p className="font-semibold text-foreground text-sm">{t.author}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.location}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      <section id="faq" className="py-20 md:py-28 px-6 bg-background">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-14">
            <span className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4 block">
              Common Questions
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Everything families want to know before getting started with pediatric speech therapy.
            </p>
          </Reveal>

          <div className="space-y-2.5">
            {faqs.map((faq, i) => (
              <Reveal key={i} delay={(i % 5) * 60} y={16} className="border border-border rounded-xl overflow-hidden bg-card">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-secondary transition-colors"
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  id={`faq-question-${i}`}
                >
                  <span className="font-semibold text-foreground text-sm pr-6 leading-snug">
                    {faq.q}
                  </span>
                  <ChevronDown
                    size={17}
                    aria-hidden="true"
                    className={`text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  id={`faq-answer-${i}`}
                  role="region"
                  aria-labelledby={`faq-question-${i}`}
                  hidden={openFaq !== i}
                  className="px-6 pb-5 pt-0.5 border-t border-border/40"
                >
                  <p className="text-sm text-muted-foreground leading-relaxed mt-3">{faq.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTACT / CTA ── */}
      <section id="contact" className="py-20 md:py-28 bg-primary px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
          <Reveal className="md:pt-4">
            <span className="text-primary-foreground/55 text-xs font-semibold tracking-[0.2em] uppercase mb-5 block">
              Get in Touch
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-primary-foreground mb-5 leading-tight">
              Ready to Help Your Child? Let's Talk.
            </h2>
            <p className="text-primary-foreground/70 leading-relaxed mb-10 text-[0.95rem]">
              Do you have questions related to your child’s speech and language development? Contact Pediatric Speech Solutions.
            </p>

            <div className="space-y-5">
              <a
                href="https://www.facebook.com/profile.php?id=61590444211789"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3.5 text-primary-foreground/75 hover:text-primary-foreground transition-colors group"
              >
                <div className="w-10 h-10 bg-primary-foreground/10 rounded-xl flex items-center justify-center group-hover:bg-primary-foreground/20 transition-colors flex-shrink-0">
                  <Users size={17} aria-hidden="true" className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-xs text-primary-foreground/60 font-medium uppercase tracking-widest mb-0.5">Facebook</p>
                  <p className="text-sm font-medium">Pediatric Speech Solutions</p>
                </div>
              </a>

              <a
                href="https://www.instagram.com/pediatricspeechsolutions"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3.5 text-primary-foreground/75 hover:text-primary-foreground transition-colors group"
              >
                <div className="w-10 h-10 bg-primary-foreground/10 rounded-xl flex items-center justify-center group-hover:bg-primary-foreground/20 transition-colors flex-shrink-0">
                  <Heart size={17} aria-hidden="true" className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-xs text-primary-foreground/60 font-medium uppercase tracking-widest mb-0.5">Instagram</p>
                  <p className="text-sm font-medium">@pediatricspeechsolutions</p>
                </div>
              </a>

              <div className="flex items-center gap-3.5 text-primary-foreground/75">
                <div className="w-10 h-10 bg-primary-foreground/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin size={17} aria-hidden="true" className="text-primary-foreground" />
                </div>
                <div>
                  <p className="text-xs text-primary-foreground/60 font-medium uppercase tracking-widest mb-0.5">Location</p>
                  <p className="text-sm font-medium">Stuart, FL &amp; Treasure Coast</p>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Contact Form */}
          <Reveal delay={150} className="bg-primary-foreground/10 backdrop-blur rounded-2xl p-7 md:p-8 border border-primary-foreground/15">
            <h3 className="font-display text-xl font-semibold text-primary-foreground mb-6">
              Send Us a Message
            </h3>
            <ContactForm />
          </Reveal>
        </div>
      </section>
      </main>

      {/* ── FOOTER ── */}
      <footer className="bg-foreground text-background px-6 pt-14 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-10 mb-12">
            <div>
              <p className="font-display text-lg font-semibold text-background mb-3">
                Pediatric Speech Solutions
              </p>
              <p className="text-sm leading-relaxed text-background/55 mb-4">
                Expert, family-centered pediatric speech therapy for children from birth through
                school age on Florida's Treasure Coast.
              </p>
              <div className="flex gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=61590444211789"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-background/50 hover:text-background/80 transition-colors underline underline-offset-2"
                >
                  Facebook
                </a>
                <span className="text-background/25" aria-hidden="true">·</span>
                <a
                  href="https://www.instagram.com/pediatricspeechsolutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-background/50 hover:text-background/80 transition-colors underline underline-offset-2"
                >
                  Instagram
                </a>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-background/50 uppercase tracking-widest mb-4">
                Services
              </p>
              <ul className="space-y-2">
                {services.map((s) => (
                  <li key={s.title}>
                    <button
                      onClick={() => scrollTo("#services")}
                      className="text-sm text-background/55 hover:text-background/85 transition-colors"
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs font-semibold text-background/50 uppercase tracking-widest mb-4">
                Service Areas
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-1.5">
                {serviceAreas.map((area) => (
                  <span key={area} className="text-sm text-background/50">
                    {area}
                  </span>
                ))}
              </div>
              <div className="mt-6">
                <p className="text-xs font-semibold text-background/50 uppercase tracking-widest mb-2">
                  Telehealth
                </p>
                <p className="text-sm text-background/50">Available statewide in Florida</p>
              </div>
            </div>
          </div>

          <div className="border-t border-background/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-background/55">
              © 2026 Pediatric Speech Solutions. All Rights Reserved.
            </p>
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4">
              <p className="text-xs text-background/55">
                Melissa Kelly, MA, CCC-SLP · Licensed Speech-Language Pathologist · Stuart, FL
              </p>
              <div className="flex items-center gap-3">
                <span className="text-background/20 hidden md:inline" aria-hidden="true">·</span>
                <a href="/privacy/" className="text-xs text-background/50 hover:text-background/80 transition-colors">
                  Privacy Policy
                </a>
                <span className="text-background/20" aria-hidden="true">·</span>
                <a href="/terms/" className="text-xs text-background/50 hover:text-background/80 transition-colors">
                  Terms of Use
                </a>
                <span className="text-background/20" aria-hidden="true">·</span>
                <button
                  type="button"
                  onClick={openConsentPreferences}
                  className="text-xs text-background/50 hover:text-background/80 transition-colors"
                >
                  Cookie Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <ConsentBanner />
    </div>
  );
}
